<template>
  <div>
    <div class="col-6 d-flex align-items-center justify-content-lg-start">
      <Button
        v-if="showBackButton && actionType === 'save' || 'add'"
        :label="$t('List page')"
        size="md"
        icon="ArrowLeftIcon"
        show-icon
        class="mb-1"
        @click="backNavigationAction"
      />
    </div>
    <b-card>
      <b-row class="position-relative">
        <b-col md="12">
          <div>
            <validation-observer ref="glPostingRules">
              <h4> {{ $t('Financial Date') }} </h4>
              <b-row>
                <b-col md="4">
                  <DatePickerField
                    id="financial-date"
                    v-model="postingFormData.postingData.financialBackdateTo"
                    rules="required"
                    :min-date="new Date()"
                    :label="$t('Pick Financial Date')"
                    :error-name="$t('Pick Financial Date')"
                    :config="datePickerConfig"
                    :placeholder="$t('Select Date')"
                  />
                </b-col>
              </b-row>
              <div class="d-flex mb-0 mt-2 align-items-center justify-content-between">
                <h4> {{ $t('Account to Debit') }} </h4>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="ml-4"
                  @click="repeatAccountToDebit"
                >
                  <feather-icon
                    icon="PlusIcon"
                    class="mr-25"
                  />
                  <span> {{ $t('Add New') }} </span>
                </b-button>
              </div>

              <b-row
                v-for="(item, index) in accountToDebitFormData"
                :id="item.id"
                :key="item.id"
                ref="accounDebitRef"
              >
                <b-col
                  md="12"
                  class="mt-0 mb-1"
                >
                  <b-form-radio-group
                    v-model="item.accountTypeDebit"
                    :options="batchAccountTypeOptions"
                    class="demo-inline-spacing"
                    :name="`radios-stacked${index}`"
                    stacked
                    value-field="value"
                    text-field="text"
                    disabled-field="disabled"
                    :disabled="isDisabled"
                  />
                </b-col>
                <b-col
                  v-if="item.accountTypeDebit === 'SavingsAndCurrent'"
                  md="2"
                >
                  <InputField
                    id="account-name"
                    v-model="item.accountName"
                    :label="$t('Search Account Name')"
                    type="text"
                    :placeholder="$t('Search Account Name')"
                    :on-input="onViewCustomerDebitSearchModal"
                  />
                </b-col>
                <b-col
                  v-if="item.accountTypeDebit === 'GeneralLedgerAccount'"
                  md="2"
                >
                  <SelectField
                    id="gl-to-debit"
                    v-model="item.glToDebit"
                    :label="$t('GL to Debit')"
                    :placeholder="$t('Select')"
                    :options="glAccountOptions"
                    :reduce="option => option.value"
                    rules="required"
                    :error-name="$t('Gl to Debit')"
                    item="label"
                    :loading="isLoadingGlAccount"
                    @input="setGLToDebitAmount(item.glToDebit, index)"
                  />
                </b-col>

                <b-col md="1">
                  <InputField
                    id="account-number"
                    v-model="item.accountNumber"
                    :label="$t('Acc. No')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="1">
                  <ValidationObserver>
                    <SelectField
                      id="sort-code-debit"
                      v-model="item.entryCode"
                      :reduce="option => option"
                      :label="$t('Sort Code')"
                      :placeholder="$t('Select')"
                      :options="debitEntryCodeOptions"
                      :error-name="$t('Sort Code')"
                      rules="required"
                      item="label"
                    />
                  </ValidationObserver>
                </b-col>
                <b-col
                  v-if="item.accountTypeDebit === 'SavingsAndCurrent'"
                  md="2"
                >
                  <SelectField
                    id="gl-to-debit"
                    v-model="item.branchCode"
                    :reduce="option => option.value"
                    :label="$t('Branch')"
                    :placeholder="$t('Select')"
                    :options="branchOptions"
                    rules="required"
                    :error-name="$t('Branch to Debit')"
                    item="label"
                    readonly
                    :loading="isLoadingBranches"
                  />
                </b-col>
                <b-col
                  v-if="item.accountTypeDebit === 'GeneralLedgerAccount'"
                  md="2"
                >
                  <SelectField
                    id="gl-to-debit"
                    v-model="item.branchCode"
                    :reduce="option => option.value"
                    :label="$t('Branch')"
                    :placeholder="$t('Select')"
                    :options="branchOptions"
                    rules="required"
                    :error-name="$t('Branch to Debit')"
                    item="label"
                    :loading="isLoadingBranches"
                  />
                </b-col>

                <b-col md="1">
                  <ValidationObserver>
                    <InputNumberField
                      id="amount"
                      v-model="item.amount"
                      :label="$t('Amount')"
                      placeholder=""
                      :error-name="$t('Amount')"
                      rules="required|positive"
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="1">
                  <ValidationObserver>
                    <InputField
                      id="instrument-no"
                      v-model="item.instrumentNumber"
                      :label="$t('Instru No')"
                      placeholder=""
                      type="text"
                      rules="required"
                      :error-name="$t('Instrument No')"
                    />
                  </ValidationObserver>
                </b-col>

                <b-col md="3">
                  <InputField
                    id="narration"
                    v-model="item.narration"
                    :label="$t('Narration')"
                    placeholder=""
                    type="text"
                    rules="required"
                    error-name="narration"
                  />
                </b-col>

                <b-col
                  md="1"
                  class="mt-2"
                >
                  <!-- Remove Button -->
                  <feather-icon
                    v-b-tooltip.hover.bottom="'Remove'"
                    icon="XIcon"
                    class="mr-25 cursor-pointer text-danger"
                    @click="removeAccountToDebitItem(index)"
                  />

                </b-col>
              </b-row>
              <b-row>
                <b-col md="3" />
                <b-col
                  md="4"
                >
                  <h6
                    class="cursor-pointer text-secondary entry-code"
                    @click="showModal('modal-primary')"
                  >
                    {{ $t('Sort code meaning?') }}
                  </h6>
                </b-col>
                <b-col md="5" />
              </b-row>

              <div class="d-flex mb-0 mt-2 align-items-center  justify-content-between">
                <h4> {{ $t('Account to Credit') }} </h4>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="ml-4"
                  @click="repeatAccountToCredit"
                >
                  <feather-icon
                    icon="PlusIcon"
                    class="mr-25"
                  />
                  <span> {{ $t('Add New') }} </span>
                </b-button>
              </div>

              <b-row
                v-for="(item, index) in accountToCreditFormData"
                :id="item.id"
                :key="item.id"
                ref="accounCreditRef"
              >
                <b-col
                  md="12"
                  class="mt-0 mb-1"
                >
                  <b-form-radio-group
                    v-model="item.accountTypeCredit"
                    :options="batchAccountTypeOptions"
                    class="demo-inline-spacing"
                    :name="`radios-stacked-${index}`"
                    stacked
                    value-field="value"
                    text-field="text"
                    disabled-field="disabled"
                    :disabled="isDisabled"
                  />
                </b-col>
                <b-col
                  v-if="item.accountTypeCredit === 'SavingsAndCurrent'"
                  md="2"
                >
                  <InputField
                    id="account-name"
                    v-model="item.accountName"
                    :label="$t('Search Account Name')"
                    type="text"
                    :placeholder="$t('Search Account Name')"
                    :on-input="onViewCustomerCreditSearchModal"
                  />
                </b-col>
                <b-col
                  v-if="item.accountTypeCredit === 'GeneralLedgerAccount'"
                  md="2"
                >
                  <SelectField
                    id="gl-to-credit"
                    v-model="item.glToCredit"
                    :reduce="option => option.value"
                    :label="$t('GL to Credit')"
                    :placeholder="$t('Select')"
                    :options="glAccountOptions"
                    :error-name="$t('GL to Credit')"
                    rules="required"
                    item="label"
                    :loading="isLoadingGlAccount"
                    @input="setGLToCreditAmount(item.glToCredit, index)"
                  />
                </b-col>

                <b-col md="1">
                  <InputField
                    id="account-number"
                    v-model="item.accountNumber"
                    :label="$t('Acc. No')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="1">
                  <SelectField
                    id="entry-code-credit"
                    v-model="item.entryCode"
                    :reduce="option => option"
                    :label="$t('Sort Code')"
                    :placeholder="$t('Select')"
                    :options="creditEntryCodeOptions"
                    :error-name="$t('Sort Code')"
                    rules="required"
                    item="label"
                  />
                </b-col>

                <b-col
                  v-if="item.accountTypeCredit === 'SavingsAndCurrent'"
                  md="2"
                >
                  <SelectField
                    id="gl-to-credit"
                    v-model="item.branchCode"
                    :reduce="option => option.value"
                    :label="$t('Branch')"
                    :placeholder="$t('Select')"
                    :options="branchOptions"
                    rules="required"
                    :error-name="$t('Branch to Credit')"
                    item="label"
                    readonly
                    :loading="isLoadingBranches"
                  />
                </b-col>
                <b-col
                  v-if="item.accountTypeCredit === 'GeneralLedgerAccount'"
                  md="2"
                >
                  <SelectField
                    id="gl-to-credit"
                    v-model="item.branchCode"
                    :reduce="option => option.value"
                    :label="$t('Branch')"
                    :placeholder="$t('Select')"
                    :options="branchOptions"
                    rules="required"
                    :error-name="$t('Branch to Credit')"
                    item="label"
                    :loading="isLoadingBranches"
                  />
                </b-col>

                <b-col md="1">
                  <InputNumberField
                    id="amount"
                    v-model="item.amount"
                    :label="$t('Amount')"
                    placeholder=""
                    :error-name="$t('Amount')"
                    rules="required|positive"
                  />
                </b-col>

                <b-col md="1">
                  <InputField
                    id="instrument-no"
                    v-model="item.instrumentNumber"
                    :label="$t('Instru No')"
                    placeholder=""
                    type="text"
                    rules="required"
                    :error-name="$t('Instrument No')"
                  />
                </b-col>

                <b-col md="3">
                  <InputField
                    id="naration"
                    v-model="item.narration"
                    :label="$t('Naration')"
                    placeholder=""
                    type="text"
                    rules="required"
                    :error-name="$t('naration')"
                  />
                </b-col>

                <b-col
                  md="1"
                  class="mt-2"
                >
                  <!-- Remove Button -->
                  <feather-icon
                    v-b-tooltip.hover.bottom="$t('Remove')"
                    icon="XIcon"
                    class="mr-25 cursor-pointer text-danger"
                    @click="removeAccountToCreditItem(index)"
                  />

                </b-col>
              </b-row>
              <b-row>
                <b-col md="3" />
                <b-col
                  md="4"
                >
                  <h6
                    class="cursor-pointer text-secondary entry-code"
                    @click="showModal('modal-primary')"
                  >
                    {{ $t('Sort code meaning?') }}
                  </h6>
                </b-col>
                <b-col md="5" />
              </b-row>

              <div class="text-center mt-5">
                <Button
                  :label="$t('Post')"
                  :loading="isLoading"
                  :disabled="isDisabled"
                  size="lg"
                  @click="handleSubmit"
                />
              </div>
            </validation-observer>
          </div>
        </b-col>
      </b-row>

      <!-- modal -->
      <b-modal
        id="modal-primary"
        ok-only
        :ok-title="$t('Close')"
        centered
        :title="$t('Sort Code Meaning')"
      >
        <b-card-text>
          <ul>
            <li
              v-for="(item, index) in entryCodes"
              :key="index"
            >
              <span>{{ item.name }}</span> -  <span>{{ item.description }}</span>
            </li>
          </ul>
        </b-card-text>
      </b-modal>

      <b-modal
        id="customer-debit-search-modal"
        :ok-title="$t('Search')"
        :cancel-title="$t('Cancel')"
        cancel-variant="outline-secondary"
        centered
        size="sm"
        :title="$t('Search Account')"
        modal-footer
        @ok="handleDebitSearch"
      >
        <validation-observer ref="searchRules">
          <b-form @submit.prevent="handleDebitSearch">
            <b-row>
              <Alert
                :show="showFormErrorAlert"
                :message="$t('Customer\'s account name or account number or customer id is required')"
              />

              <b-col
                md="12"
                class="mt-3"
              >
                <InputField
                  id="account-number"
                  v-model="searchParams.accountNumber"
                  :label="$t('Account Number')"
                  :placeholder="$t('Enter account number')"
                  type="text"
                  for="account-number"
                />
              </b-col>
              <b-col md="12">
                <InputField
                  id="customer-name"
                  v-model="searchParams.accountName"
                  :label="$t('Account Name')"
                  :placeholder="$t('Enter account name')"
                  type="text"
                  error-name=""
                  rules=""
                />
              </b-col>
              <b-col
                md="12"
              >
                <InputField
                  id="customer-id"
                  v-model="searchParams.customerID"
                  :label="$t('Customer ID')"
                  :placeholder="$t('Enter customer id')"
                  type="text"
                  for="customer-id"
                />
              </b-col>
              <b-col md="12">
                <InputField
                  id="reference-number"
                  v-model="searchParams.referenceNumber"
                  :label="$t('Reference Number')"
                  :placeholder="$t('Enter reference number')"
                  type="text"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-modal>
      <b-modal
        id="customer-credit-search-modal"
        :ok-title="$t('Search')"
        :cancel-title="$t('Cancel')"
        cancel-variant="outline-secondary"
        centered
        size="sm"
        :title="$t('Search Account')"
        modal-footer
        @ok="handleCreditSearch"
      >
        <validation-observer ref="searchRules">
          <b-form @submit.prevent="handleCreditSearch">
            <b-row>
              <Alert
                :show="showFormErrorAlert"
                :message="$t('Customer\'s account name or account number or customer id is required')"
              />

              <b-col
                md="12"
                class="mt-3"
              >
                <InputField
                  id="account-number"
                  v-model="searchParams.accountNumber"
                  :label="$t('Account Number')"
                  :placeholder="$t('Enter account number')"
                  type="text"
                  for="account-number"
                />
              </b-col>
              <b-col md="12">
                <InputField
                  id="customer-name"
                  v-model="searchParams.accountName"
                  :label="$t('Account Name')"
                  :placeholder="$t('Enter account name')"
                  type="text"
                  error-name=""
                  rules=""
                />
              </b-col>
              <b-col
                md="12"
              >
                <InputField
                  id="customer-id"
                  v-model="searchParams.customerID"
                  :label="$t('Customer ID')"
                  :placeholder="$t('Enter customer id')"
                  type="text"
                  for="customer-id"
                />
              </b-col>
              <b-col md="12">
                <InputField
                  id="reference-number"
                  v-model="searchParams.referenceNumber"
                  :label="$t('Reference Number')"
                  :placeholder="$t('Enter reference number')"
                  type="text"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-modal>

      <b-modal
        id="search-debit-modal"
        ref="search-debit-modal"
        :cancel-title="$t('Cancel')"
        cancel-variant="outline-secondary"
        centered
        size="lg"
        :title="$t('Customer Account Details')"
      >
        <b-table
          ref="customerListTable"
          select-mode="single"
          :items="customerList"
          :per-page="pageSize"
          :current-page="currentPage"
          responsive
          :fields="translatedTableHeaders"
          primary-key="id"
          selectable
          show-empty
          :empty-text="$t('No result found')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="position-relative"
          @row-selected="onSelectDebitCustomer"
        >
          <template #cell(SN)="data">
            {{ getOverallIndex(data.index) }}
          </template>
        </b-table>

        <TablePagination
          v-model="currentPage"
          :from="from"
          :to="to"
          :total-rows="totalEntries"
          :per-page="perPage"
        />

        <template
          #modal-footer="{ }"
        >
          <div />
        </template>
      </b-modal>

      <b-modal
        id="search-credit-modal"
        ref="search-credit-modal"
        :cancel-title="$t('Cancel')"
        cancel-variant="outline-secondary"
        centered
        size="lg"
        :title="$t('Customer Account Details')"
      >
        <b-table
          ref="customerListTable"
          select-mode="single"
          :items="customerList"
          :per-page="pageSize"
          :current-page="currentPage"
          responsive
          :fields="translatedTableHeaders"
          primary-key="id"
          selectable
          show-empty
          :empty-text="$t('No result found')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="position-relative"
          @row-selected="onSelectCreditCustomer"
        >
          <template #cell(SN)="data">
            {{ getOverallIndex(data.index) }}
          </template>
        </b-table>

        <TablePagination
          v-model="currentPage"
          :from="from"
          :to="to"
          :total-rows="totalEntries"
          :per-page="perPage"
        />

        <template
          #modal-footer="{ }"
        >
          <div />
        </template>
      </b-modal>

      <FullScreenSpinner :show="showSpinner" />
    </b-card>
  </div>
</template>

<script>

import {
  BCard, BRow, BCol, VBTooltip, BButton, BModal, BCardText, BFormRadioGroup, BForm, BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { required, positive } from '@validations'
import { ValidationObserver } from 'vee-validate'
import moment from 'moment'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toKobo } from '@/helpers/formatAmount'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import Button from '../../../components/button/Button.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import Alert from '../../../components/alert/Alert.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { getMinusOneDayDate } from '@/helpers/formatDate'

const today = new Date()
const dateTime = moment(today).format()

export default {
  components: {
    BRow,
    BCol,
    BModal,
    InputField,
    Button,
    SelectField,
    InputNumberField,
    BCard,
    FullScreenSpinner,
    ValidationObserver,
    BButton,
    BCardText,
    BFormRadioGroup,
    Alert,
    BForm,
    BTable,
    TablePagination,
    DatePickerField,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
    'height-fade': heightFade,
  },
  mixins: [sweetAlert, modalMixins, tableMixins],
  data() {
    return {
      isLoading: false,
      isLoadingBranches: false,
      isLoadingGlAccount: false,
      isDisabled: false,
      showFormErrorAlert: false,
      showCustomerInfo: false,
      required,
      positive,
      showSpinner: false,
      showBackButton: false,
      sortBy: 'accountName',
      sortDesc: false,
      currentPage: 1,
      selected: [],
      perPage: 10,
      glAccountOptions: [],
      entryCodes: [
        { name: 'CHQW', description: 'Cheque Withdrawal' },
        { name: 'CHQC', description: 'Cheque Clearing' },
        { name: 'CHQD', description: 'Cheque deposit' },
        { name: 'CSHD', description: 'Cash deposit' },
        { name: 'CSHW', description: 'Cash Withdrawal' },
        { name: 'JRNL', description: 'Journal Posting' },
        { name: 'BCSH', description: 'Buy Cash' },
        { name: 'SCSH', description: 'Sell Cash' },
        { name: 'IBRH', description: 'Inter-branch' },
        { name: 'LTRF', description: 'Local Transfer' },
        { name: 'CLOS', description: 'Account Closure' },
        { name: 'FAAQ', description: 'Fixed Asset Acquisition' },
        { name: 'FADP', description: 'Fixed Asset Depreciation' },
        { name: 'FACT', description: 'Value Added Tax Deduction' },
        { name: 'FAAD', description: 'Fixed Asset Accumulated Depreciation @ Liquidation' },
        { name: 'FALQ', description: 'Fixed Asset Liquidation' },
        { name: 'VATD', description: 'Value Added Tax Deduction' },
      ],
      debitEntryCode: 'D-JRNL',
      creditEntryCode: 'C-JRNL',
      debitEntryCodeOptions: ['D-CSHD', 'D-CHQD1A', 'D-CHQD1B', 'D-CHQW', 'D-CSHW', 'D-JRNL', 'D-BCSH', 'D-SCSH', 'D-IBRH', 'D-LTRF'],
      creditEntryCodeOptions: ['C-CSHD', 'C-CHQD1A', 'C-CHQD1B', 'C-CHQW', 'C-CSHW', 'C-JRNL', 'C-BCSH', 'C-SCSH', 'C-IBRH', 'C-LTRF'],
      depositData: {
        accountNumber: '',
        amount: '',
        recordType: 'Debit',
        entryCode: '',
        narration: '',
        instrumentNumber: '',
        branchCode: '',
      },
      postingFormData: {
        postingData: {
          realDate: dateTime,
          financialBackdateTo: null,
          postingType: 'BatchGLandCustomerBackdatingPosting',
          initiatedBy: '',
        },
      },
      accountToDebitFormData: [{
        accountNumber: null,
        accountName: null,
        glToDebit: null,
        amount: null,
        recordType: 'Debit',
        narration: '',
        instrumentNumber: '',
        entryCode: '',
        branchCode: '',
        accountTypeDebit: 'SavingsAndCurrent',
      }],
      accountToCreditFormData: [{
        accountNumber: null,
        accountName: null,
        glToCredit: null,
        amount: null,
        recordType: 'Credit',
        narration: '',
        instrumentNumber: '',
        entryCode: '',
        branchCode: '',
        accountTypeCredit: 'SavingsAndCurrent',
      }],
      posting: [],
      financialDate: null,
      // branchOptions: [],
      isAccountTypeDebit: true,
      isAccountTypeCredit: true,
      customer: {
        customerID: '',
        accountNumber: '',
      },
      // datePickerConfig: {
      //   dateFormat: 'Y-m-d',
      // },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      searchParams: {
        customerID: '',
        accountName: '',
        accountNumber: '',
        referenceNumber: '',
      },
      batchAccountTypeOptions: [
        { text: 'Savings/Current', value: 'SavingsAndCurrent', disabled: false },
        { text: 'General Ledger', value: 'GeneralLedgerAccount', disabled: false },
      ],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
      ],
      customerList: [],
      customerSearchPagination: {
        currentPage: 1,
        from: this.customerSearchFrom,
        to: this.customerSearchTo,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      allGLAccounts: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    getInstitutionFinancialDate() {
      return getMinusOneDayDate(this.currentFinancialDate.financialDate)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  mounted() {
    this.fetchGLAccount()
    this.datePickerConfig.maxDate = this.getInstitutionFinancialDate
  },
  methods: {
    ...mapActions({
      getAllBranches: 'operations/getAllBranches',
      getGLAccount: 'account/getAllGLAccount',
      postCashDeposit: 'posting/postingBackdatingAction',
      getCustomer: 'account/searchCustomerAccountAction',
      customerAccount: 'account/getCustomerAccountList',
    }),

    async setGLToDebitAmount(selectedValue, index) {
      if (selectedValue !== 'Select') {
        await this.getAllBranches({ permission: 'VIEW_BACKDATING_POSTING' })
        this.accountToDebitFormData[index].accountNumber = selectedValue
        const debitGlAccountName = this.allGLAccounts.filter(item => item.code === Number(selectedValue)).reduce((acc, curr) => ({ label: curr.name }), {})
        this.accountToDebitFormData[index].accountName = debitGlAccountName.label
      }
      return selectedValue
    },
    async setGLToCreditAmount(selectedValue, index) {
      if (selectedValue !== 'Select') {
        await this.getAllBranches({ permission: 'VIEW_BACKDATING_POSTING' })
        this.accountToCreditFormData[index].accountNumber = selectedValue
        const creditGlAccountName = this.allGLAccounts.filter(item => item.code === Number(selectedValue)).reduce((acc, curr) => ({ label: curr.name }), {})
        this.accountToCreditFormData[index].accountName = creditGlAccountName.label
      }
      return selectedValue
    },

    async fetchGLAccount() {
      try {
        this.isLoadingGlAccount = true
        const response = await this.getGLAccount({ permission: 'VIEW_BACKDATING_POSTING' })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.glAccountOptions = data.data.filter(item => item.status === 'Active')
            .reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }],

              // eslint-disable-next-line no-nested-ternary
              []).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))

          this.allGLAccounts = data.data.filter(item => item.status === 'Active')
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingGlAccount = false
      }
    },
    async handleSubmit() {
      this.$refs.glPostingRules.validate().then(success => {
        if (success) {
          const accountToDebitFormData = this.accountToDebitFormData.map(item => item.amount).reduce((a, b) => a + b, 0)
          const accountToCreditFormData = this.accountToCreditFormData.map(item => item.amount).reduce((a, b) => a + b, 0)

          if (accountToDebitFormData === accountToCreditFormData) {
            this.confirm(this.glPostingAction, { text: this.$t(this.$t('Are you sure you want to initiate this Backdating Posting?')) })
          } else {
            this.alertError({ text: this.$t('Amount to Debit and Credit must be equal') })
          }
        }
      })
    },
    async glPostingAction() {
      try {
        const postingApprovalModel = {
          amount: toKobo(this.accountToDebitFormData.map(item => item.amount).reduce((a, b) => a + b, 0)),
          initiatedBy: '',
          approvedBy: '',
          postingType: 'BatchGLandCustomerBackdatingPosting',
        }
        const formData = this.accountToDebitFormData.concat(this.accountToCreditFormData)
        this.postingFormData.postingData.initiatedBy = this.userDetails.email
        this.postingFormData.postingEntry = formData.map(item => ({
          ...item,
          amount: toKobo(item.amount),
        }))
        this.postingFormData.postingApprovalModel = postingApprovalModel
        this.isLoading = true
        this.isDisabled = true
        const postingData = this.postingFormData
        const response = await this.postCashDeposit(postingData)

        const { status, data } = await response

        if (status === 200 && data.isSuccessful) {
          this.resetInputField()
          const backdatingRequest = this.$t('Your Backdating request was successful. Backdating reference ID is')
          this.alertSuccess({ text: `${backdatingRequest} ${data.referenceNumber}` })
          this.$router.push({ name: 'backdating-posting' })
        }

        if (status === 200 && !data.isSuccessful) {
          this.isLoading = false
          this.isDisabled = false
          const failedText = this.$t('Your Backdating request failed. Reason:')
          this.alertError({ text: `${failedText} ${data.message}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    showEntryCodeModal() {

    },
    repeatAccountToDebit() {
      this.accountToDebitFormData.push({
        accountNumber: null,
        accountName: null,
        glToDebit: null,
        amount: null,
        recordType: 'Debit',
        narration: '',
        instrumentNumber: '',
        entryCode: '',
        branchCode: '',
        accountTypeDebit: 'SavingsAndCurrent',
      })

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.accounDebitRef[0].offsetHeight)
      })
    },
    repeatAccountToCredit() {
      this.accountToCreditFormData.push({
        accountNumber: null,
        accountName: null,
        glToCredit: null,
        amount: null,
        recordType: 'Credit',
        narration: '',
        instrumentNumber: '',
        entryCode: '',
        branchCode: '',
        accountTypeCredit: 'SavingsAndCurrent',
      })

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.accountCreditRef[0].offsetHeight)
      })
    },
    removeAccountToCreditItem(index) {
      this.accountToCreditFormData.splice(index, 1)
      this.trTrimHeight(this.$refs.accountCreditRef[0].offsetHeight)
    },
    removeAccountToDebitItem(index) {
      this.accountToDebitFormData.splice(index, 1)
      this.trTrimHeight(this.$refs.accounDebitRef[0].offsetHeight)
    },
    resetInputField() {
      this.accountToCreditFormData = []
      this.accountToDebitFormData = []
    },
    onViewCustomerDebitSearchModal() {
      this.$root.$emit('bv::show::modal', 'customer-debit-search-modal')
    },
    onViewCustomerCreditSearchModal() {
      this.$root.$emit('bv::show::modal', 'customer-credit-search-modal')
    },
    handleDebitSearch() {
      try {
        this.$refs.searchRules.validate().then(async success => {
          if (success) {
            const isSearchParamsNotEmpty = Object.values(
              this.searchParams,
            ).every(params => params === null || params === '')
            if (!isSearchParamsNotEmpty) {
              this.showFormErrorAlert = false
              this.showSpinner = true
              const hasFetchedCustomer = await this.fetchCustomer()
              this.showSpinner = false
              if (hasFetchedCustomer) {
                this.$root.$emit('bv::show::modal', 'search-debit-modal')
              }
            } else {
              this.showFormErrorAlert = true
            }
          }
        })
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    handleCreditSearch() {
      try {
        this.$refs.searchRules.validate().then(async success => {
          if (success) {
            const isSearchParamsNotEmpty = Object.values(
              this.searchParams,
            ).every(params => params === null || params === '')
            if (!isSearchParamsNotEmpty) {
              this.showFormErrorAlert = false
              this.showSpinner = true
              const hasFetchedCustomer = await this.fetchCustomer()
              this.showSpinner = false
              if (hasFetchedCustomer) {
                this.$root.$emit('bv::show::modal', 'search-credit-modal')
              }
            } else {
              this.showFormErrorAlert = true
            }
          }
        })
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async fetchCustomer() {
      try {
        const response = await this.getCustomer({
          search: this.searchParams,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_BACKDATING_POSTING',
        })

        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.customerList = data.data.map(item => ({
            ...item,
            customerName: item.name,
            phoneNumber: item.customerType === 'Individual' ? item.phoneNumber : item.businessPhoneNumber,
          }))
          this.customerSearchPagination.totalEntries = data.recordCount
          return true
        }
        this.alertError({ text: this.$t('No record found') })
        return false
      } catch (error) {
        this.alertError({ text: error })
      }
      return false
    },

    async getLinkedDebitCustomerAccounts() {
      try {
        this.hideModal('customer-debit-search-modal')
        const response = await this.customerAccount({
          search: { accountNumber: this.customer.accountNumber },
          page: 0,
          limit: 0,
          permission: 'VIEW_BACKDATING_POSTING',
        })

        const { data, status } = await response
        if (status === 200 && data.data !== null) {
          const allDebitIndexes = [...this.accountToDebitFormData.keys()]
          const debitIndex = Math.max.apply(null, allDebitIndexes)
          this.accountToDebitFormData[debitIndex].accountNumber = data.data.reduce((acc, cur) => acc + cur.accountNumber, [])
          this.accountToDebitFormData[debitIndex].accountName = data.data.reduce((acc, cur) => acc + cur.accountName, [])
          this.accountToDebitFormData[debitIndex].branchCode = data.data.reduce((acc, cur) => acc + cur.branchCode, [])
        } else {
          this.alertError({ text: this.$t('No linked accounts found') })
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getLinkedCreditCustomerAccounts() {
      try {
        this.hideModal('customer-debit-search-modal')
        const response = await this.customerAccount({
          // search: { customerID: this.customer.customerID },
          search: { accountNumber: this.customer.accountNumber },
          page: 0,
          limit: 0,
          permission: 'VIEW_BACKDATING_POSTING',
        })

        const { data, status } = await response
        if (status === 200 && data.data !== null) {
          const allCreditIndexes = [...this.accountToCreditFormData.keys()]
          const creditIndex = Math.max.apply(null, allCreditIndexes)
          this.accountToCreditFormData[creditIndex].accountNumber = data.data.reduce((acc, cur) => acc + cur.accountNumber, [])
          this.accountToCreditFormData[creditIndex].accountName = data.data.reduce((acc, cur) => acc + cur.accountName, [])
          this.accountToCreditFormData[creditIndex].branchCode = data.data.reduce((acc, cur) => acc + cur.branchCode, [])
        } else {
          this.alertError({ text: this.$t('No linked accounts found') })
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async onSelectDebitCustomer(items) {
      this.hideModal('search-debit-modal')
      this.showSpinner = true
      const customerInfo = { ...items[0] }
      // this.customer.customerID = customerInfo.customerID
      this.customer.accountNumber = customerInfo.accountNumber
      this.showCustomerInfo = true
      this.showSearchButton = true
      await this.getLinkedDebitCustomerAccounts()
      this.showCustomerInfo = true
      this.showSearchButton = true
      this.showSpinner = false
    },
    async onSelectCreditCustomer(items) {
      this.hideModal('search-credit-modal')
      this.showSpinner = true
      const customerInfo = { ...items[0] }
      this.customer.accountNumber = customerInfo.accountNumber
      this.showCustomerInfo = true
      this.showSearchButton = true
      await this.getLinkedCreditCustomerAccounts()
      this.showCustomerInfo = true
      this.showSearchButton = true
      this.showSpinner = false
    },
    backNavigationAction() {
      this.$router.push({
        name: 'backdating-posting',
      })
    },
  },
}
</script>
<style scoped>
 [dir] .demo-inline-spacing > *, .custom-control , .custom-control-inline, .custom-checkbox {
    margin-top: 0rem !important;
}
.entry-code {
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  font-style: italic;

}
</style>
